import { FiltroPractica, UserPhotoData } from './types';
import { createSelector } from 'reselect';
import { RootState } from 'store/types';
import { initialState } from 'store/usuarios/reducer';
import {
  State,
  PerfilesUsuario,
  PerfilUsuario,
  InfoUser,
  DatosPerfilesUsuario,
  Perfil,
} from 'store/usuarios/types';
import { RequestStatus } from 'types';
import { makeSelectProfileSelected } from 'store/app/selectors';
import { RequestStatusTypes } from 'consts';
import { userProfile } from 'utils/profilesIndex';
import { EstadoPractica } from 'store/practicas/types';
import { makeSelectEstadosPracticas } from 'store/practicas/selectors';

const selectState = (state: RootState) => state.usuarios || initialState;

// Directly reducer
export const makeSelectPerfilesUsuario = () =>
  createSelector(
    selectState,
    (state: State): PerfilesUsuario => state.perfilesUsuario
  );

export const makeSelectInfoUsers = () =>
  createSelector(selectState, (state: State): InfoUser[] => state.infoUsers);

// Combined
export const makeSelectPerfilesUsuarioPerfiles = () =>
  createSelector(
    makeSelectPerfilesUsuario(),
    (perfilesUsuario: PerfilesUsuario): Perfil[] => perfilesUsuario.PERFILES
  );

export const makeSelectPerfilesUsuarioDatosPerfilesUsuario = () =>
  createSelector(
    makeSelectPerfilesUsuario(),
    (perfilesUsuario: PerfilesUsuario): DatosPerfilesUsuario[] => {
      return perfilesUsuario.DATOS_PERFILES_USUARIO;
    }
  );

export const makeSelectPerfilesUsuarioDatosPerfilesUsuarioSelected = () =>
  createSelector(
    makeSelectPerfilesUsuarioDatosPerfilesUsuario(),
    makeSelectProfileSelected(),
    (
      datosPerfilesUsuario: DatosPerfilesUsuario[],
      selectedIndex: number
    ): DatosPerfilesUsuario => datosPerfilesUsuario[selectedIndex]
  );

export const makeSelectIsProfileActive = () =>
  createSelector(
    makeSelectPerfilesUsuarioDatosPerfilesUsuarioSelected(),
    (profileActive: DatosPerfilesUsuario): boolean => {
      return profileActive?.ACTIVO === 'S';
    }
  );

export const makeSelectIsStudentProfile = () =>
  createSelector(
    makeSelectPerfilesUsuarioDatosPerfilesUsuarioSelected(),
    (profileActive: DatosPerfilesUsuario): boolean => {
      return (
        profileActive?.ID_PERFIL === userProfile.alumno.extAspirante ||
        profileActive?.ID_PERFIL === userProfile.alumno.extBecario ||
        profileActive?.ID_PERFIL === userProfile.alumno.extMaquinista ||
        profileActive?.ID_PERFIL === userProfile.alumno.intBecario ||
        profileActive?.ID_PERFIL === userProfile.alumno.intMaquinista
      );
    }
  );

export const makeSelectIsManagerProfile = () =>
  createSelector(
    makeSelectPerfilesUsuarioDatosPerfilesUsuarioSelected(),
    (profileActive: DatosPerfilesUsuario): boolean => {
      return (
        profileActive?.ID_PERFIL === userProfile.formador ||
        profileActive?.ID_PERFIL === userProfile.gestor ||
        profileActive?.ID_PERFIL === userProfile.administrador
      );
    }
  );


export const makeSelectIsViewerProfile = () =>
  createSelector(
    makeSelectPerfilesUsuarioDatosPerfilesUsuarioSelected(),
    (profileActive: DatosPerfilesUsuario): boolean => {
      return (
        profileActive?.ID_PERFIL === userProfile.supervisor ||
        profileActive?.ID_PERFIL === userProfile.visualizador
      );
    }
  );


export const makeSelectIsAdminGestorProfile = () =>
  createSelector(
    makeSelectPerfilesUsuarioDatosPerfilesUsuarioSelected(),
    (profileActive: DatosPerfilesUsuario): boolean => {
      return (
        profileActive?.ID_PERFIL === userProfile.administrador ||
        profileActive?.ID_PERFIL === userProfile.gestor
      );
    }
  );

export const makeSelectIsAdministradorProfile = () =>
  createSelector(
    makeSelectPerfilesUsuarioDatosPerfilesUsuarioSelected(),
    (profileActive: DatosPerfilesUsuario): boolean => {
      return profileActive?.ID_PERFIL === userProfile.administrador;
    }
  );

export const makeSelectIsFormadorProfile = () =>
  createSelector(
    makeSelectPerfilesUsuarioDatosPerfilesUsuarioSelected(),
    (profileActive: DatosPerfilesUsuario): boolean => {
      return profileActive?.ID_PERFIL === userProfile.formador;
    }
  );

export const makeSelectIsInstructorProfile = () =>
  createSelector(
    makeSelectPerfilesUsuarioDatosPerfilesUsuarioSelected(),
    (profileActive: DatosPerfilesUsuario): boolean => {
      return profileActive?.ID_PERFIL === userProfile.instructor;
    }
  );

export const makeSelectPerfilesUsuarioDatosPerfilesUsuarioSelectedPerfil = () =>
  createSelector(
    makeSelectPerfilesUsuarioPerfiles(),
    makeSelectPerfilesUsuarioDatosPerfilesUsuarioSelected(),
    (
      perfiles: Perfil[],
      datosPerfilUsuarioSelected: DatosPerfilesUsuario
    ): Perfil => {
      return (
        perfiles
          .filter(
            (perfil: Perfil) =>
              perfil.ID_PERFIL === datosPerfilUsuarioSelected?.ID_PERFIL
          )
          .shift() || ({} as Perfil)
      );
    }
  );


export const makeSelectPerfilesUsuarioDatosPerfilesUsuarioPerfiles = () =>
  createSelector(
    makeSelectPerfilesUsuarioPerfiles(),
    makeSelectPerfilesUsuarioDatosPerfilesUsuario(),
    (
      perfiles: Perfil[],
      datosPerfilesUsuario: DatosPerfilesUsuario[]
    ): Perfil[] => {
      return perfiles.filter(
        (perfil: Perfil) =>
          datosPerfilesUsuario.filter(
            (datosPerfilUsuario: DatosPerfilesUsuario) =>
              perfil.ID_PERFIL === datosPerfilUsuario.ID_PERFIL
          ).length > 0
      );
    }
  );
/** Add validations: perfil.NOMBRE_PERFIL delete type Alumn */
export const makeSelectPerfilesUsuarioDatosPerfilesUsuarioPerfilesParsed = () =>
  createSelector(
    makeSelectPerfilesUsuarioDatosPerfilesUsuarioPerfiles(),
    makeSelectPerfilesUsuarioDatosPerfilesUsuarioSelectedPerfil(),
    (perfiles: Perfil[], perfilSelected: Perfil): string => {
      return JSON.stringify(
        perfiles.map((perfil: Perfil) => ({
          active: perfil.ID_PERFIL === perfilSelected.ID_PERFIL,
          label: perfil.NOMBRE_PERFIL,
          ref: perfil.ID_PERFIL,
        }))
      );
    }
  );

export const makeSelectPerfilesUsuarioDatosPerfilesUsuarioIdPerfil = () =>
  createSelector(
    makeSelectPerfilesUsuarioDatosPerfilesUsuario(),
    (DATOS_PERFILES_USUARIO: DatosPerfilesUsuario[]): string => {
      return (
        DATOS_PERFILES_USUARIO.filter(
          (e: DatosPerfilesUsuario) => e.ID_PERFIL
        ).shift()?.ID_PERFIL || ''
      );
    }
  );

export const makeSelectPerfilesUsuarioIdUsuario = () =>
  createSelector(
    makeSelectPerfilesUsuarioDatosPerfilesUsuario(),
    (DATOS_PERFILES_USUARIO: DatosPerfilesUsuario[]): string =>
      DATOS_PERFILES_USUARIO.filter(
        (e: DatosPerfilesUsuario) => e.ID_USUARIO
      ).shift()?.ID_USUARIO || ''
  );

export const makeSelectPerfilesUsuarioNombreUsuario = () =>
  createSelector(
    makeSelectPerfilesUsuarioDatosPerfilesUsuario(),
    (DATOS_PERFILES_USUARIO: DatosPerfilesUsuario[]): string =>
      DATOS_PERFILES_USUARIO.filter(
        (e: DatosPerfilesUsuario) => e.NOMBRE_USUARIO
      ).shift()?.NOMBRE_USUARIO || ''
  );

export const makeSelectNombreInstructor = () =>
  createSelector(
    makeSelectPerfilesUsuarioDatosPerfilesUsuario(),
    (DATOS_PERFILES_USUARIO: DatosPerfilesUsuario[]) => {
      return DATOS_PERFILES_USUARIO.filter(
        (e: DatosPerfilesUsuario) => e.ID_PERFIL === '211'
      ).shift()?.NOMBRE_USUARIO;
    }
  );

// Requests statuses
export const makeSelectGetPerfilesUsuarioRequestStatus = () =>
  createSelector(
    selectState,
    (state: State): RequestStatus => state.getPerfilesUsuarioRequestStatus
  );

export const makeSelectGetPerfilesUsuarioRequestStatusSuccess = () =>
  createSelector(
    makeSelectGetPerfilesUsuarioRequestStatus(),
    (requestStatus: RequestStatus): boolean =>
      requestStatus.status === RequestStatusTypes.Success
  );

export const makeSelectGetInfoUsersRequestStatus = () =>
  createSelector(
    selectState,
    (state: State): RequestStatus => state.getInfoUsersRequestStatus
  );

// User photo
export const makeSelectGetUserPhoto = () =>
  createSelector(selectState, (state: State): string =>
    state.userPhoto ? state.userPhoto : './images/user.png'
  );

export const makeSelectGetUserInfoData = () =>
  createSelector(
    selectState,
    (state: State): UserPhotoData => state.userPhotoData
  );

export const makeSelectUpdateUserPortraitRequestStatus = () =>
  createSelector(
    selectState,
    (state: State): RequestStatus => state.updateUserPortraitRquestStatus
  );

export const makeSelectGetAlumnPhoto = () =>
  createSelector(selectState, (state: State): string =>
    state.alumnPhoto ? state.alumnPhoto : './images/user.png'
  );

//Filtros
export const makeSelectFiltros = () =>
  createSelector(
    selectState,
    (state: State): FiltroPractica[] => state.filtros
  );

export const makesSelectFiltrosParse = () =>
  createSelector(
    makeSelectFiltros(),
    (filtroPractica: FiltroPractica[]): string =>
      JSON.stringify(
        filtroPractica.map((filtro: FiltroPractica) => ({
          label: filtro.ID_FILTRO + ' - ' + filtro.NOMBRE_FILTRO,
          value: filtro.FILTRO_JSON,
        }))
      )
  );

const getEstadoFiltroOption = (
  estadoId: string,
  estadosPractica: EstadoPractica[]
) => {
  const estado = estadosPractica.find(
    (item) => item.ID_EST_PRACTICA === estadoId
  );

  return estado
    ? {
        label: estado.NOMBRE_EST_PRACTICA,
        value: estado.ID_EST_PRACTICA,
      }
    : {
        label: estadoId,
        value: estadoId,
      };
};

const parseEstadosFiltro = (
  e: FiltroPractica,
  estadosPractica: EstadoPractica[]
) => {
  let filtroData;

  try {
    filtroData = JSON.parse(e.FILTRO_JSON);
  } catch (error) {
    console.error(error);
    return [];
  }

  if (!filtroData?.estados) {
    return [];
  }

  return filtroData.estados
    .split(',')
    .map((estadoId: string) =>
      getEstadoFiltroOption(estadoId, estadosPractica)
    );
};

export const makeSelectEstadosFiltro = () =>
  createSelector(
    makeSelectEstadosPracticas(),
    makeSelectFiltros(),
    (
      estadosPractica: EstadoPractica[],
      filtroPractica: FiltroPractica[]
    ): string =>
      JSON.stringify(
        filtroPractica
          .map((e: FiltroPractica) =>
            parseEstadosFiltro(e, estadosPractica)
          )
          .flat()
      )
  );

export const makeSelectFiltroId = () =>
  createSelector(selectState, (state: State): string => state.filtroId);
export const makeSelectNombrePagina = () =>
  createSelector(selectState, (state: State): string => state.nombrePagina);
export const makeSelectNombreFiltro = () =>
  createSelector(selectState, (state: State): string => state.nombreFiltro);
export const makeSelectFechaCreacion = () =>
  createSelector(selectState, (state: State): number => state.fechaCreacion);
export const makeSelectFechaModificacion = () =>
  createSelector(selectState, (state: State): number => state.fechaModificacion);
export const makeSelectFiltroJson = () =>
  createSelector(selectState, (state: State): string => state.filtroJson);
export const makeSelectGuardarFiltroRequestStatus = () =>
  createSelector(
    selectState,
    (state: State): RequestStatus => state.guardarFiltroRequestStatus
  );

  //panel administracion
  export const makeSelectAllUsuarios = () =>
  createSelector(
    [selectState],
    (state: State): PerfilUsuario[] => state.usuariosAll
  );

  export const makeSelectGetAllUsuariosRequestStatus = () =>
  createSelector(
    selectState,
    (state: State): RequestStatus => state.getAllUsuariosRequestStatus
  );

  export const makeSelectpostUsuarioPerfilRequestStatus =
    makeSelectGetAllUsuariosRequestStatus;


export const makeSelectdisabledUsuarioPerfilRequestStatus =
  makeSelectGetAllUsuariosRequestStatus;
